import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Users,
  Car,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  Activity,
  Loader2
} from 'lucide-react';
import {
  useTodayTrips,
  useTodayRevenue,
  useTodayGMV,
  useOnDutyDriverList,
  useActiveRides,
  useTodayRideHistory,
} from '@/api-services/hooks/useDashboard';
import { ApiResponse, PagedData } from '@/api-services/hooks/types';
import type { RideHistoryItem } from '@/api-services/hooks/dashboardTypes';

// Helper function to extract data from API response
const extractData = <T,>(response: ApiResponse<T> | null): T | null => {
  if (!response) return null;
  if (response.data) return response.data as T;
  return null;
};

// Helper function to format currency
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format number
const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

const Dashboard = () => {
  // Fetch dashboard data
  const { data: todayTripsResponse, loading: tripsLoading, error: tripsError } = useTodayTrips();
  const { data: todayRevenueResponse, loading: revenueLoading, error: revenueError } = useTodayRevenue();
  const { data: todayGMVResponse, loading: gmvLoading, error: gmvError } = useTodayGMV();
  const { data: onDutyDriversResponse, loading: driversLoading, error: driversError } = useOnDutyDriverList();
  const { data: activeRidesResponse, loading: activeRidesLoading, error: activeRidesError } = useActiveRides();
  const { data: rideHistoryResponse, loading: rideHistoryLoading, error: rideHistoryError } = useTodayRideHistory();

  // Extract data from responses
  const tripsData = extractData(todayTripsResponse);
  const revenueData = extractData(todayRevenueResponse);
  const gmvData = extractData(todayGMVResponse);
  const onDutyDrivers = extractData(onDutyDriversResponse) || [];
  const activeRides = extractData(activeRidesResponse) || [];
  
  // Extract ride history data
  const rideHistoryData = extractData(rideHistoryResponse) as PagedData<RideHistoryItem> | null;
  const rideHistoryItems = rideHistoryData?.content || rideHistoryData || [];

  // Calculate revenue from trips data or ride history
  const calculatedRevenueFromTrips = tripsData?.totalRevenue || tripsData?.totalFare || 0;
  
  // Calculate revenue from ride history (sum of all fares)
  const calculatedRevenueFromRides = useMemo(() => {
    if (!Array.isArray(rideHistoryItems)) return 0;
    
    return rideHistoryItems.reduce((sum: number, ride: RideHistoryItem) => {
      // Try different possible field names for fare
      const fare = ride.fare || ride.totalFare || ride.amount || ride.revenue || 0;
      // Only count completed rides
      if (ride.status === 'COMPLETED' || ride.status === 'completed' || !ride.status) {
        return sum + (typeof fare === 'number' ? fare : 0);
      }
      return sum;
    }, 0);
  }, [rideHistoryItems]);

  // Use revenue from API if available, otherwise calculate from trips or ride history
  const totalRevenue = revenueData?.totalRevenue || 
                      revenueData?.todayRevenue || 
                      calculatedRevenueFromTrips || 
                      calculatedRevenueFromRides || 
                      0;
  
  const totalGMV = gmvData?.totalGMV || gmvData?.todayGMV || 0;
  const totalTrips = tripsData?.totalTrips || tripsData?.completedTrips || 0;
  const activeDriversCount = onDutyDrivers.length;
  const activeRidesCount = activeRides.length;

  // Prepare chart data (using trips data for ride status)
  const rideStatusData = useMemo(() => {
    const completed = tripsData?.completedTrips || 0;
    const active = tripsData?.activeTrips || activeRidesCount;
    const cancelled = tripsData?.cancelledTrips || 0;
    
    return [
      { name: 'Completed', value: completed, color: 'hsl(var(--success))' },
      { name: 'Active', value: active, color: 'hsl(var(--primary))' },
      { name: 'Cancelled', value: cancelled, color: 'hsl(var(--destructive))' },
    ].filter(item => item.value > 0);
  }, [tripsData, activeRidesCount]);

  // Loading state - include ride history loading for revenue calculation
  const isLoading = tripsLoading || revenueLoading || gmvLoading || driversLoading || activeRidesLoading || rideHistoryLoading;
  const hasError = tripsError || revenueError || gmvError || driversError || activeRidesError || rideHistoryError;
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here's what's happening with your fleet.</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="animate-glow">
            <Activity className="w-3 h-3 mr-1" />
            Live
          </Badge>
          <Button className="bg-gradient-primary hover:opacity-90">
            <MapPin className="w-4 h-4 mr-2" />
            View Live Map
          </Button>
        </div>
      </div>

      {/* Error State */}
      {hasError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>Error loading dashboard data. Please try again later.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Today's Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-chart-accent" />
          </CardHeader>
          <CardContent>
            {(revenueLoading || rideHistoryLoading) ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatCurrency(totalRevenue)}</div>
                <p className="text-xs text-muted-foreground flex items-center mt-1">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {calculatedRevenueFromRides > 0 
                    ? `Calculated from ${rideHistoryItems.length} rides`
                    : calculatedRevenueFromTrips > 0
                    ? 'From trips data'
                    : 'Today\'s total revenue'}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">On-Duty Drivers</CardTitle>
            <Car className="h-4 w-4 text-chart-primary" />
          </CardHeader>
          <CardContent>
            {driversLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatNumber(activeDriversCount)}</div>
                <p className="text-xs text-success flex items-center mt-1">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Currently on duty
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Today's Trips</CardTitle>
            <Users className="h-4 w-4 text-chart-secondary" />
          </CardHeader>
          <CardContent>
            {tripsLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatNumber(totalTrips)}</div>
                <p className="text-xs text-muted-foreground flex items-center mt-1">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Total trips today
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="hover-lift interactive border-metrics-border bg-metrics-bg/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Rides</CardTitle>
            <Activity className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            {activeRidesLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-foreground">{formatNumber(activeRidesCount)}</div>
                <p className="text-xs text-warning flex items-center mt-1">
                  <Clock className="w-3 h-3 mr-1" />
                  Currently in progress
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GMV Chart */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle className="text-foreground">Today's GMV</CardTitle>
          </CardHeader>
          <CardContent>
            {gmvLoading ? (
              <div className="h-80 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="h-80 chart-enter">
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-foreground mb-2">
                      {formatCurrency(totalGMV)}
                    </div>
                    <p className="text-sm text-muted-foreground">Gross Merchandise Value</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ride Status Chart */}
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle className="text-foreground">Ride Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {tripsLoading ? (
              <div className="h-80 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : rideStatusData.length === 0 ? (
              <div className="h-80 flex items-center justify-center">
                <p className="text-muted-foreground">No ride data available</p>
              </div>
            ) : (
              <div className="h-80 chart-enter">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={rideStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {rideStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active Rides List */}
      {activeRides.length > 0 && (
        <Card className="hover-lift">
          <CardHeader>
            <CardTitle className="text-foreground">Active Rides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeRides.slice(0, 5).map((ride: any, index: number) => (
                <div key={ride.rideId || index} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="w-2 h-2 rounded-full mt-2 bg-primary animate-glow" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground">
                      Ride #{ride.rideId || 'N/A'} - {ride.status || 'In Progress'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Driver ID: {ride.driverId || 'N/A'} | Rider ID: {ride.riderId || 'N/A'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;